import React, { useState } from 'react';
import { string, shape, number, bool, func, arrayOf, object } from 'prop-types';
import { propTypes } from '../../util/types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import {
  NamedLink,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
} from '../../components';
import MenuIcon from './MenuIcon';
import css from './WishlistCard.module.css';

// Match ManageListingCard menu behavior and placement.
const MENU_CONTENT_OFFSET = -12;
const MOBILE_MAX_WIDTH = 550;

const WishlistCard = props => {
  const history = useHistory();
  const intl = useIntl();
  const {
    wishlist,
    className,
    rootClassName,
    currentUser,
    onEdit,
    onDelete,
    onShare,
    onTogglePublic,
    isTogglingPublic,
    navigateTo,
  } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (!wishlist) {
    return null;
  }

  const {
    id,
    title,
    subtitle,
    items,
    isPublic,
    numberOfGuests,
    budget,
    description,
    coverImageUrl,
    coverImageThumbnails,
  } = wishlist;

  const classes = classNames(rootClassName || css.root, className);
  const itemCount = items ? items.length : 0;
  const targetPath = navigateTo || `/wishlists/${id}`;
  const sharedWithEntries = Array.isArray(wishlist.sharedWith)
    ? wishlist.sharedWith.filter(
        entry =>
          Boolean(
            entry && (entry.recipientId || entry.userId || entry.recipientName || entry.name || entry.email)
          )
      )
    : [];
  const sharedWithNames = sharedWithEntries
    .map(entry => entry.recipientName || entry.name || entry.email)
    .filter(Boolean);
  const visibleSharedWithNames = sharedWithNames.slice(0, 2);
  const additionalSharedWithCount = Math.max(sharedWithNames.length - visibleSharedWithNames.length, 0);

  // Extract image from description if present
  const extractImageFromDescription = (desc) => {
    if (!desc) return null;
    
    // Match both markdown image syntax ![alt](url) and HTML <img> tags
    const markdownMatch = desc.match(/!\[.*?\]\((.*?)\)/);
    const htmlMatch = desc.match(/<img[^>]+src="([^">]+)"/);
    
    if (markdownMatch && markdownMatch[1]) {
      return markdownMatch[1];
    }
    if (htmlMatch && htmlMatch[1]) {
      return htmlMatch[1];
    }
    return null;
  };

  // Determine which image to show: coverImageUrl first, then description image
  const coverImage = coverImageUrl || extractImageFromDescription(description);
  const hasCoverImage = !!coverImage;

  return (
    <div className={classes}>
      <div
        className={css.clickWrapper}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          history.push(targetPath);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            history.push(targetPath);
          }
        }}
      >
        <div className={css.aspectRatioWrapper}>
          <div className={css.coverImage}>
            {hasCoverImage ? (
              <div className={css.image}>
                <img
                  src={coverImage}
                  srcSet={
                    coverImageThumbnails
                      ? `${coverImageThumbnails.mobile} 480w,
                         ${coverImageThumbnails.tablet} 768w,
                         ${coverImageThumbnails.desktop} 1920w`
                      : undefined
                  }
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt={title}
                  className={css.coverImageImg}
                />
              </div>
            ) : (
              <div className={css.placeholderImage}>
                <div className={css.placeholderContent}>
                  <img
                    src="/static/images/DefaultPicture_ToastyList.svg"
                    alt=""
                    className={css.placeholderIcon}
                  />
                </div>
              </div>
            )}
            <div className={css.menubarWrapper}>
              <div className={css.menubarGradient} />
              {(onEdit || onDelete) && (
              <div className={css.menubar}>
                <Menu
                  className={classNames(css.menu, { [css.cardIsOpen]: true })}
                  contentPlacementOffset={MENU_CONTENT_OFFSET}
                  mobileMaxWidth={MOBILE_MAX_WIDTH}
                  contentPosition="right"
                  useArrow={false}
                  onToggleActive={isOpen => {
                    setIsMenuOpen(isOpen);
                  }}
                  isOpen={isMenuOpen}
                >
                  <MenuLabel className={css.menuLabel} isOpenClassName={css.menuLabelOpen}>
                    <div
                      className={css.iconWrapper}
                      role="button"
                      aria-label={intl.formatMessage({ id: 'WishlistCard.optionsMenu' })}
                    >
                      <MenuIcon className={css.menuIcon} isActive={isMenuOpen} />
                    </div>
                  </MenuLabel>
                  <MenuContent rootClassName={css.menuContent} contentClassName={css.menuList}>
                    {onEdit && (
                      <MenuItem key="edit-wishlist">
                        <button
                          type="button"
                          className={css.menuIconButton}
                          aria-label={intl.formatMessage({ id: 'WishlistDetailPage.editTitle' })}
                          title={intl.formatMessage({ id: 'WishlistDetailPage.editTitle' })}
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsMenuOpen(false);
                            onEdit(event);
                          }}
                        >
                          <FaPencilAlt className={css.menuActionIcon} aria-hidden="true" />
                        </button>
                      </MenuItem>
                    )}
                    {onDelete && (
                      <MenuItem key="delete-wishlist">
                        <button
                          type="button"
                          className={css.menuIconButton}
                          aria-label={intl.formatMessage({ id: 'WishlistCard.delete' })}
                          title={intl.formatMessage({ id: 'WishlistCard.delete' })}
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsMenuOpen(false);
                            onDelete(event);
                          }}
                        >
                          <FaTrashAlt className={css.menuActionIcon} aria-hidden="true" />
                        </button>
                      </MenuItem>
                    )}
                  </MenuContent>
                </Menu>
              </div>
              )}
            </div>

            {onTogglePublic ? (
              <div className={css.iconTopRight}>
                <button
                  className={css.lockButton}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onTogglePublic(e);
                  }}
                  aria-label={
                    isPublic
                      ? intl.formatMessage({ id: 'WishlistDetailPage.makePrivate' })
                      : intl.formatMessage({ id: 'WishlistDetailPage.makePublic' })
                  }
                  title={
                    isPublic
                      ? intl.formatMessage({ id: 'WishlistDetailPage.makePrivate' })
                      : intl.formatMessage({ id: 'WishlistDetailPage.makePublic' })
                  }
                  type="button"
                  disabled={isTogglingPublic}
                >
                  {isPublic ? (
                    <FaLockOpen className={css.lockIcon} aria-hidden="true" />
                  ) : (
                    <FaLock className={css.lockIcon} aria-hidden="true" />
                  )}
                </button>
              </div>
            ) : null}



            {/* Share icon */}
            {onShare && (
              <div className={css.iconBottomRight}>
                <button
                  className={css.shareButton}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onShare();
                  }}
                  aria-label={intl.formatMessage({ id: 'WishlistCard.share' })}
                  type="button"
                >
                  <svg
                    className={css.shareIcon}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M22 2 11 13" />
                    <path d="M22 2 15 22l-4-9-9-4Z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={css.info}>
        <div className={css.title}>{title}</div>
        {subtitle && <div className={css.subtitle}>{subtitle}</div>}
        
        <div className={css.metadata}>
          <span className={css.itemCount}>
            <FormattedMessage
              id="WishlistCard.itemCount"
              values={{ count: itemCount }}
            />
          </span>
          
          {numberOfGuests && (
            <span className={css.guests}>
              • <FormattedMessage
                id="WishlistCard.guests"
                values={{ count: numberOfGuests }}
              />
            </span>
          )}
          
          {budget && budget.amount && (
            <span className={css.budget}>
              • {new Intl.NumberFormat('de-CH', {
                style: 'currency',
                currency: budget.currency || 'CHF',
              }).format(budget.amount / 100)}
            </span>
          )}
        </div>

        {visibleSharedWithNames.length > 0 ? (
          <div className={css.sharedWithRow}>
            <FormattedMessage
              id="WishlistCard.sharedWith"
              values={{ names: visibleSharedWithNames.join(', ') }}
            />
            {additionalSharedWithCount > 0 ? (
              <span className={css.sharedWithMore}>
                {' '}
                +{additionalSharedWithCount}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

WishlistCard.defaultProps = {
  className: null,
  rootClassName: null,
  onEdit: null,
  onDelete: null,
  onShare: null,
  onTogglePublic: null,
  isTogglingPublic: false,
  navigateTo: null,
};

WishlistCard.propTypes = {
  wishlist: shape({
    id: string.isRequired,
    title: string.isRequired,
    subtitle: string,
    description: string,
    coverImageUrl: string,
    coverImageThumbnails: shape({
      mobile: string,
      tablet: string,
      desktop: string,
    }),
    items: arrayOf(object),
    isPublic: bool,
    numberOfGuests: number,
    budget: shape({
      amount: number,
      currency: string,
    }),
  }).isRequired,
  currentUser: propTypes.currentUser,
  className: string,
  rootClassName: string,
  onEdit: func,
  onDelete: func,
  onShare: func,
  onTogglePublic: func,
  isTogglingPublic: bool,
  navigateTo: string,
};

export default WishlistCard;
